// The single shader the map draws with.
//
// One program covers every primitive — filled geometry, analytic discs and
// rings, distance-field text and bitmaps — selected per vertex. That is what
// lets a whole map object go out in one draw call whatever it is made of, which
// is the property the SVG renderer could not have.

const VERTEX_SOURCE = `
attribute vec2 aPos;
attribute vec2 aUV;
attribute vec4 aColor;
attribute vec2 aExtra;

// Rows of the batch-space to view-pixel affine transform.
uniform vec3 uRow0;
uniform vec3 uRow1;

// A card flipping about its vertical axis - a 3D rotation and perspective
// divide in batch space, ahead of the affine transform. x carries cos of the
// angle times the shrink, y the shrink alone, z sin over the viewer distance;
// (1, 1, 0) is flat and costs one multiply and a divide by one.
uniform vec3 uFlip;

// View pixels to clip space. The map's viewBox is centred on the origin, so this
// is a pure scale.
uniform vec2 uViewScale;

uniform float uTime;

varying vec2 vUV;
varying vec4 vColor;
varying vec2 vExtra;
varying float vAge;

const float PARTICLE_LIFE = 2.4;

void main() {
  vUV = aUV;
  vColor = aColor;
  vExtra = aExtra;
  vAge = 0.0;

  vec2 pos = aPos;

  if (aExtra.x > 5.5 && aExtra.x < 6.5) {
    // A particle carries a seed and where it sits in the emitter's cycle, packed
    // into one number. Its whole path is then a function of the clock, so a plume
    // of a few hundred costs one draw and no per-frame work at all.
    float seed = floor(aExtra.y);
    float phase = fract(aExtra.y);
    float age = fract(uTime / PARTICLE_LIFE + phase);
    vAge = age;

    // Rising, spreading as it goes, with a slow waver so no two follow the same
    // line. Local units, applied before the transform, so it drifts with whatever
    // the object is doing.
    float spread = sin(seed * 12.9898) * 0.9 + sin(seed * 4.1414 + uTime) * 0.35;
    pos.x += spread * age * 26.0;
    pos.y -= age * (34.0 + fract(seed * 0.618) * 22.0);
  }

  pos = vec2(pos.x * uFlip.x, pos.y * uFlip.y) / (1.0 + pos.x * uFlip.z);

  vec2 view = vec2(
    uRow0.x * pos.x + uRow0.y * pos.y + uRow0.z,
    uRow1.x * pos.x + uRow1.y * pos.y + uRow1.z
  );

  gl_Position = vec4(view * uViewScale, 0.0, 1.0);
}
`;

const FRAGMENT_BODY = `
precision highp float;

uniform sampler2D uAtlas;

// Opacity of the batch as a whole. Kept out of the vertex colours so that the
// object fades the map runs constantly — every arrival and every scope's exit —
// cost a uniform write rather than a geometry rebuild.
uniform float uAlpha;

// Seconds, wrapped well short of where a float stops being able to resolve a
// frame. Only the pulse mode reads it.
uniform float uTime;

varying vec2 vUV;
varying vec4 vColor;
varying vec2 vExtra;
varying float vAge;

// How many pulses ride a link at once, and how fast they travel along it.
const float PULSE_COUNT = 3.0;
const float PULSE_SPEED = 0.55;

// How far the line dims between pulses. The steady line dropping back is what
// makes the pulses read as something moving along it rather than as a pattern
// drawn on top, so this is most of the way down.
const float PULSE_TROUGH = 0.12;

// A link only ever lightly loaded still shows its traffic clearly: how busy it is
// sets the rate, not whether it can be seen at all. Scaling the contrast by load
// as well left most links looking barely touched.
const float PULSE_FLOOR = 0.55;

void main() {
  float mode = vExtra.x;
  vec3 rgb = vColor.rgb;
  float alpha = vColor.a;

  if (mode > 0.5 && mode < 1.5) {
    // Analytic disc or ring: the coverage falls out of the distance to the unit
    // circle, so it stays exact however far the camera zooms in.
    float d = length(vUV);
    float w = EDGE_WIDTH(d);
    alpha *= 1.0 - smoothstep(1.0 - w, 1.0 + w, d);
    if (vExtra.y > 0.0) {
      alpha *= smoothstep(vExtra.y - w, vExtra.y + w, d);
    }
  } else if (mode > 1.5 && mode < 2.5) {
    float d = texture2D(uAtlas, vUV).a;
    float w = max(EDGE_WIDTH(d), 0.002);
    alpha *= smoothstep(vExtra.y - w, vExtra.y + w, d);
  } else if (mode > 2.5 && mode < 3.5) {
    vec4 texel = texture2D(uAtlas, vUV);
    rgb = texel.rgb;
    alpha = texel.a * vColor.a;
  } else if (mode > 3.5 && mode < 4.5) {
    // Work travelling along a link, from the parent end towards the child.
    //
    // The head is where the phase wraps, and raising the ramp to a power leaves
    // a tail stretched out behind it — a comet rather than a bead. Subtracting
    // the travel term rather than adding it is what sends them outwards.
    float busy = vExtra.y;
    float phase = fract(vUV.x * PULSE_COUNT - uTime * PULSE_SPEED);
    float comet = pow(1.0 - phase, 6.0);

    // An idle link is left exactly as it was, so switching the effect on changes
    // nothing about the parts of the map where nothing is happening.
    float depth = mix(PULSE_FLOOR, 1.0, busy) * (1.0 - PULSE_TROUGH);
    alpha *= 1.0 - depth + depth * comet;

    // Darkened at the head as well as brought up to full opacity. On a light
    // background opacity alone has very little room to work in.
    rgb *= 1.0 - 0.35 * comet;
  } else if (mode > 5.5 && mode < 6.5) {
    // Fire and smoke. Soft round falloff, fading as it goes, and hottest at the
    // start of its life — the colour is handed in per particle, so a smoke plume
    // is the same shader with a duller one.
    float d = length(vUV);
    alpha *= 1.0 - smoothstep(0.25, 1.0, d);
    alpha *= (1.0 - vAge) * (1.0 - vAge);
  }
  alpha *= uAlpha;
  if (alpha <= 0.0) discard;

  // Premultiplied, so a single blend setting covers ordinary compositing and the
  // inverting blend the SQL widget's labels use.
  fragColor = vec4(rgb * alpha, alpha);
}
`;

// One shader source serves both GLSL versions through a preamble. WebGL2 needs
// ES 3.00 rather than ES 1.00 because the derivative functions the antialiasing
// depends on are core there and, on an ES 1.00 shader in a WebGL2 context,
// unreachable — the OES_standard_derivatives extension that would enable them is
// not exposed.
function preludes(es3, hasDerivatives) {
  if (es3) {
    return {
      vertex: "#version 300 es\n#define attribute in\n#define varying out\n",
      fragment:
        "#version 300 es\n" +
        // Ahead of the output declaration: a fragment shader has no default
        // float precision in ES 3.00, and `out vec4` needs one.
        "precision highp float;\n" +
        "#define varying in\n" +
        "#define texture2D texture\n" +
        "out vec4 fragColor;\n" +
        "#define EDGE_WIDTH(d) fwidth(d)\n",
    };
  }

  // Without derivatives there is no way to know how wide an edge is in device
  // pixels, so the fallback picks a fixed narrow band.
  const edge = hasDerivatives
    ? "#extension GL_OES_standard_derivatives : enable\n#define EDGE_WIDTH(d) fwidth(d)\n"
    : "#define EDGE_WIDTH(d) 0.02\n";

  return {
    vertex: "",
    fragment: edge + "#define fragColor gl_FragColor\n",
  };
}

export function createProgram(gl, es3, hasDerivatives) {
  const prelude = preludes(es3, hasDerivatives);

  const vertex = compile(gl, gl.VERTEX_SHADER, prelude.vertex + VERTEX_SOURCE);
  const fragment = compile(
    gl,
    gl.FRAGMENT_SHADER,
    prelude.fragment + FRAGMENT_BODY
  );

  const program = gl.createProgram();
  gl.attachShader(program, vertex);
  gl.attachShader(program, fragment);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error("map shader link failed: " + log);
  }

  gl.deleteShader(vertex);
  gl.deleteShader(fragment);

  return {
    program,
    attributes: {
      position: gl.getAttribLocation(program, "aPos"),
      uv: gl.getAttribLocation(program, "aUV"),
      colour: gl.getAttribLocation(program, "aColor"),
      extra: gl.getAttribLocation(program, "aExtra"),
    },
    uniforms: {
      row0: gl.getUniformLocation(program, "uRow0"),
      row1: gl.getUniformLocation(program, "uRow1"),
      flip: gl.getUniformLocation(program, "uFlip"),
      viewScale: gl.getUniformLocation(program, "uViewScale"),
      atlas: gl.getUniformLocation(program, "uAtlas"),
      alpha: gl.getUniformLocation(program, "uAlpha"),
      time: gl.getUniformLocation(program, "uTime"),
    },
  };
}

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error("map shader compile failed: " + log + "\n" + source);
  }

  return shader;
}
